import { useState } from 'react'
import { useI18n } from '../../i18n'
import { useWebsite } from '../../hooks/use-website'
import { Bot, ToggleLeft, ToggleRight, MessageCircle, Upload } from 'lucide-react'
import type { WebsiteButler } from '../../types'

interface Props {
  butler: WebsiteButler
  onUpdate: () => void
}

export default function ButlerConfigPanel({ butler, onUpdate }: Props) {
  const { t } = useI18n()
  const { updateButler } = useWebsite()
  const [form, setForm] = useState(butler)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await updateButler(form)
    onUpdate()
    setSaving(false)
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Bot className="h-4 w-4 text-orange-500" />
        <span className="text-sm font-medium text-[#1a1a1a]">{t('AI管家配置', 'AI Butler Config')}</span>
      </div>

      <div className="flex items-center justify-between p-3 rounded-xl bg-black/[0.02]">
        <span className="text-xs text-gray-500">{t('启用管家', 'Enable Butler')}</span>
        <button onClick={() => setForm(prev => ({ ...prev, enabled: !prev.enabled }))}>
          {form.enabled ? (
            <ToggleRight className="h-6 w-6 text-orange-500" />
          ) : (
            <ToggleLeft className="h-6 w-6 text-gray-300" />
          )}
        </button>
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('管家名称', 'Bot Name')}</label>
        <input
          value={form.botName}
          onChange={(e) => setForm(prev => ({ ...prev, botName: e.target.value }))}
          className="w-full px-3 py-2 rounded-xl border border-black/[0.06] text-xs bg-white focus:border-orange-200 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('欢迎语', 'Welcome Message')}</label>
        <textarea
          value={form.welcomeMessage}
          onChange={(e) => setForm(prev => ({ ...prev, welcomeMessage: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 rounded-xl border border-black/[0.06] text-xs bg-white focus:border-orange-200 focus:outline-none resize-none"
        />
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('位置', 'Position')}</label>
        <div className="flex gap-2">
          {(['bottom-right', 'bottom-left'] as const).map(pos => (
            <button key={pos} onClick={() => setForm(prev => ({ ...prev, position: pos }))}
              className={`flex-1 py-2 text-xs rounded-xl border transition-all ${
                form.position === pos
                  ? 'border-orange-200 bg-orange-50 text-orange-600 font-medium'
                  : 'border-black/[0.06] text-gray-400 hover:text-[#1a1a1a]'
              }`}>
              {pos === 'bottom-right' ? t('右下角', 'Right') : t('左下角', 'Left')}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('应用ID (appId)', 'App ID')}</label>
        <input
          value={form.appId || ''}
          onChange={(e) => setForm(prev => ({ ...prev, appId: e.target.value }))}
          placeholder={t('关联知识库应用', 'Linked knowledge base app')}
          className="w-full px-3 py-2 rounded-xl border border-black/[0.06] text-xs bg-white focus:border-orange-200 focus:outline-none"
        />
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full py-2 rounded-xl bg-[#1a1a1a] text-white text-xs font-medium hover:bg-[#333] disabled:opacity-50 transition-all">
        {saving ? t('保存中...', 'Saving...') : t('保存配置', 'Save Config')}
      </button>

      {/* Preview */}
      <div className="mt-4 p-4 rounded-2xl bg-gray-50 border border-black/[0.04]">
        <p className="text-[10px] text-gray-400 mb-3">{t('管家预览', 'Preview')}</p>
        <div className="bg-white rounded-2xl border border-black/[0.04] p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-orange-500" />
            </div>
            <span className="text-xs font-medium text-[#1a1a1a]">{form.botName || 'AI管家'}</span>
          </div>
          <div className="px-3 py-2 rounded-xl bg-orange-50 text-xs text-gray-600 max-w-[80%]">
            {form.welcomeMessage || t('有什么可以帮您？', 'How can I help you?')}
          </div>
        </div>
      </div>
    </div>
  )
}
